import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Printer, Download, AlertCircle, FileText } from 'lucide-react';
import { GlobalWorkerOptions } from 'pdfjs-dist';
import { Root, Pages, Page, CanvasLayer } from '@anaralabs/lector';
import { useDebounce } from 'use-debounce';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Switch } from 'components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui';
import { Label } from 'components/ui/label';
import * as TestModule from 'modules/test';
import { useTranslation } from 'react-i18next';
import { http } from 'services';
import { useToast } from 'components/ui/use-toast';
import { cn } from 'lib/utils';
import { FEATURES, useFeature, useOpenUpgradeForFeature } from 'modules/entitlements';
import { UpgradePrompt } from 'components/entitlements';

// Bundle the worker from the locally-installed pdfjs-dist package instead of
// fetching a hardcoded version off an external CDN — a same-origin asset URL
// so it can't be blocked by CSP/third-party restrictions, and it can never
// drift out of sync with the installed dependency version.
GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

type FontSize = 'small' | 'medium' | 'large' | 'xlarge';

interface WorksheetOptions {
  shuffleQuestions: boolean;
  shuffleAnswers: boolean;
  includeAnswerKey: boolean;
  fontSize: FontSize;
  showPoints: boolean;
  showQuestionNumbers: boolean;
  columns: 1 | 2;
}

const DEFAULT_OPTIONS: WorksheetOptions = {
  shuffleQuestions: false,
  shuffleAnswers: false,
  includeAnswerKey: true,
  fontSize: 'medium',
  showPoints: true,
  showQuestionNumbers: true,
  columns: 1,
};

// Font-size choices rendered as scaled "A" glyphs (small → extra large).
const FONT_SIZES: { value: FontSize; cls: string; labelKey: string }[] = [
  { value: 'small', cls: 'text-xs', labelKey: 'small' },
  { value: 'medium', cls: 'text-base', labelKey: 'medium' },
  { value: 'large', cls: 'text-xl', labelKey: 'large' },
  { value: 'xlarge', cls: 'text-3xl', labelKey: 'extra_large' },
];

const buildParams = (o: WorksheetOptions): Record<string, string> => ({
  shuffleQuestions: String(o.shuffleQuestions),
  shuffleAnswers: String(o.shuffleAnswers),
  includeAnswerKey: String(o.includeAnswerKey),
  fontSize: o.fontSize,
  showPoints: String(o.showPoints),
  showQuestionNumbers: String(o.showQuestionNumbers),
  columns: String(o.columns),
});

const Spinner: React.FC = () => (
  <div className="flex items-center justify-center h-full">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-foreground" />
  </div>
);

// Renders the actual worksheet PDF so the preview is byte-identical to the
// downloaded/printed output (single source of truth = the server template).
const WorksheetPdfPreview: React.FC<{ url: string }> = ({ url }) => (
  <Root
    key={url}
    source={url}
    className="h-full w-full"
    isZoomFitWidth
    zoomOptions={{ minZoom: 0.5, maxZoom: 3 }}
    loader={<Spinner />}
  >
    <Pages className="p-4">
      <Page>
        <CanvasLayer />
      </Page>
    </Pages>
  </Root>
);

export const WorksheetPage: React.FC = () => {
  const { t } = useTranslation();
  const { testId } = useParams<{ testId: string }>();
  const { toast } = useToast();

  const [options, setOptions] = useState<WorksheetOptions>(DEFAULT_OPTIONS);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const blobRef = useRef<string | null>(null);

  // Worksheet PDF export is a paid capability (Growth+). The whole page — preview
  // and download — drives the gated export endpoint, so gate it at the page level
  // (an upgrade prompt) rather than letting the preview 403 on load.
  const canWorksheet = useFeature(FEATURES.WORKSHEET_PDF);
  const openUpgrade = useOpenUpgradeForFeature();

  const { item: test, isLoading } = TestModule.Hooks.useSingle({
    id: testId || '',
    enabled: Boolean(testId),
  });
  const updateConfig = TestModule.Hooks.useUpdateConfig();

  // Seed the controls from the persisted worksheet defaults (like sibling config pages).
  useEffect(() => {
    if (test?.worksheet) {
      setOptions({ ...DEFAULT_OPTIONS, ...test.worksheet });
    }
  }, [test]);

  // Debounce the (re)render so toggling controls doesn't fire a request per tick.
  const [debouncedOptions] = useDebounce(options, 400);

  useEffect(() => {
    // Don't fire the (gated) export endpoint when the org isn't entitled — the
    // page renders an upgrade prompt instead of the preview.
    if (!testId || !canWorksheet) return;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(false);
    http.request
      .get(`/api/exports/worksheet/${testId}`, {
        params: buildParams(debouncedOptions),
        responseType: 'blob',
      })
      .then((res) => {
        if (cancelled) return;
        const url = URL.createObjectURL(res.data as Blob);
        if (blobRef.current) URL.revokeObjectURL(blobRef.current);
        blobRef.current = url;
        setPreviewUrl(url);
      })
      .catch(() => {
        if (!cancelled) setPreviewError(true);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [testId, debouncedOptions, canWorksheet]);

  // Revoke the last blob URL on unmount.
  useEffect(
    () => () => {
      if (blobRef.current) URL.revokeObjectURL(blobRef.current);
    },
    [],
  );

  // Authenticated fetch (window.open would omit the Authorization header → 401).
  const fetchWorksheetBlob = async (): Promise<Blob> => {
    const res = await http.request.get(`/api/exports/worksheet/${testId}`, {
      params: buildParams(options),
      responseType: 'blob',
    });
    return res.data as Blob;
  };

  const handleDownload = async () => {
    try {
      const blob = await fetchWorksheetBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${test?.title || 'worksheet'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('error'), { description: t('worksheet_generation_failed') });
    }
  };

  const handlePrint = async () => {
    try {
      const blob = await fetchWorksheetBlob();
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      if (!printWindow) {
        URL.revokeObjectURL(url);
        return;
      }
      // Revoke after printing (afterprint), with a fallback so the object URL
      // never leaks if the user just closes the print dialog.
      const revoke = () => URL.revokeObjectURL(url);
      printWindow.addEventListener('afterprint', revoke);
      printWindow.onload = () => printWindow.print();
      setTimeout(revoke, 60000);
    } catch {
      toast.error(t('error'), { description: t('worksheet_generation_failed') });
    }
  };

  const handleSave = async () => {
    if (!testId) return;
    await updateConfig.mutateAsync({
      id: testId,
      step: 'worksheet',
      data: options as unknown as Record<string, unknown>,
    });
  };

  const setOption = <K extends keyof WorksheetOptions>(key: K, value: WorksheetOptions[K]) =>
    setOptions((prev) => ({ ...prev, [key]: value }));

  if (isLoading || !test) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Spinner />
      </div>
    );
  }

  if (!canWorksheet) {
    return (
      <div className="flex items-center justify-center min-h-[300px] p-4">
        <UpgradePrompt
          featureKey={FEATURES.WORKSHEET_PDF}
          requiredPlan="GROWTH"
          icon={FileText}
          description={t(
            'worksheet_premium_desc',
            'Turn any test into a print-ready worksheet PDF — shuffle questions, add an answer key, and hand out paper copies.',
          )}
          onUpgrade={() => openUpgrade(FEATURES.WORKSHEET_PDF)}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:h-[calc(100vh-200px)]">
      {/* Preview Panel — the real PDF */}
      <div className="flex flex-col min-w-0 lg:flex-1 h-[60vh] lg:h-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">{t('worksheet_preview')}</h2>
          {previewLoading && <span className="text-sm text-muted-foreground">{t('loading_pdf')}</span>}
        </div>

        <Card className="flex-1 overflow-hidden bg-gray-100">
          <CardContent className="p-0 h-full">
            {previewError ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-6">
                <AlertCircle className="h-12 w-12 text-red-500 mb-3" />
                <p className="text-muted-foreground max-w-md">{t('worksheet_generation_failed')}</p>
              </div>
            ) : previewUrl ? (
              <WorksheetPdfPreview url={previewUrl} />
            ) : (
              <Spinner />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Controls Panel */}
      <div className="w-full lg:w-80 space-y-4 lg:overflow-y-auto">
        <Card>
          <CardContent className="p-4 space-y-4">
            <h3 className="font-semibold">{t('worksheet_options')}</h3>

            <div className="flex items-center justify-between">
              <Label>{t('shuffle_questions')}</Label>
              <Switch
                checked={options.shuffleQuestions}
                onChange={(checked) => setOption('shuffleQuestions', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label>{t('shuffle_answers')}</Label>
              <Switch
                checked={options.shuffleAnswers}
                onChange={(checked) => setOption('shuffleAnswers', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label>{t('include_answer_key')}</Label>
              <Switch
                checked={options.includeAnswerKey}
                onChange={(checked) => setOption('includeAnswerKey', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label>{t('show_points')}</Label>
              <Switch checked={options.showPoints} onChange={(checked) => setOption('showPoints', checked)} />
            </div>

            <div className="flex items-center justify-between">
              <Label>{t('show_question_numbers')}</Label>
              <Switch
                checked={options.showQuestionNumbers}
                onChange={(checked) => setOption('showQuestionNumbers', checked)}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('font_size')}</Label>
              <div role="group" aria-label={t('font_size')} className="grid grid-cols-4 gap-2">
                {FONT_SIZES.map(({ value, cls, labelKey }) => {
                  const active = options.fontSize === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setOption('fontSize', value)}
                      aria-pressed={active}
                      aria-label={t(labelKey)}
                      title={t(labelKey)}
                      className={cn(
                        'flex items-center justify-center h-11 rounded-md border transition-colors focus:outline-none focus:ring-2 focus:ring-ring',
                        active
                          ? 'border-foreground bg-foreground text-background'
                          : 'border-input text-muted-foreground hover:bg-accent hover:text-foreground',
                      )}
                    >
                      <span className={cn('font-semibold leading-none', cls)}>A</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('columns')}</Label>
              <Select
                value={String(options.columns)}
                onValueChange={(v) => setOption('columns', Number(v) === 2 ? 2 : 1)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">{t('one_column')}</SelectItem>
                  <SelectItem value="2">{t('two_columns')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              className="w-full"
              variant="outline"
              onClick={handleSave}
              disabled={updateConfig.isPending}
            >
              {updateConfig.isPending && (
                <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
              )}
              {t('save')}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 space-y-3">
            <h3 className="font-semibold">{t('actions')}</h3>

            <Button className="w-full" onClick={handleDownload}>
              <Download className="h-4 w-4 mr-2" />
              {t('download_pdf')}
            </Button>

            <Button variant="outline" className="w-full" onClick={handlePrint}>
              <Printer className="h-4 w-4 mr-2" />
              {t('print')}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WorksheetPage;
